<template>
  <form
    v-on:submit.prevent="onSubmit"
    class="form-create-clinic-document-template">
    <template-variables-info v-on:click-download-button="onClickDownloadVariables" />
    <autocomplete-input
      label="Категория"
      item-value="id"
      item-text="name"
      v-bind:items=categories
      v-bind:violations="categoryInputErrors"
      v-model="categoryId"
    />
    <div>
      <input-file
        title="Файл шаблона"
        required
        v-on:download-file="onDownloadFile"
        v-bind:accept="$options.acceptList"
        v-bind:with-view-button="true"
        v-on:view-file="onViewFile"
        v-model="file"
        v-bind:violations="fileInputErrors"
      />
      <p class="form-create-clinic-document-template__addiction-info">не более 10мб, файлы .docx</p>
    </div>
    <input-text
      title="Название"
      required
      v-model="name"
      maxlength="250"
      v-bind:violations="nameInputErrors" />
    <input-select
      title="Тип"
      option-value-key="value"
      option-title-key="title"
      v-bind:options="templateTypes"
      v-model="type"
    />
    <!--    <input-checkbox-->
    <!--      title="Необходимо подтверждение в ЕСИА"-->
    <!--      v-model="isEsiaConfirmationRequired" />-->
    <div class="form-create-clinic-document-template__buttons">
      <button
        class="form-create-clinic-document-template__buttons-cancel"
        v-on:click="() => $emit('cancel')"
        type="button">Отмена</button>
      <button
        class="form-create-clinic-document-template__buttons-submit"
        v-bind:disabled="disableUpload"
        type="submit">Сохранить</button>
    </div>
  </form>
</template>

<script>
import InputText from '@/components/InputText.vue';
import InputSelect from '@/components/InputSelect.vue';
import InputFile from '@/components/InputFile.vue';
import { ClinicTemplateRequestCreate, ClinicTemplateRequestPreview } from '@/storage/gateways/DocumentTemplateGateway';
import clinicTemplateMixin, { CONTRACT_TYPE, DOCUMENT_TYPE } from '@/mixins/clinicTemplateMixin';
import notifierMixin from '@/mixins/notifierMixin';
import TemplateVariablesInfo from '@/components/TemplateVariablesInfo.vue';
import AutocompleteInput from '@/components/AutocompleteInput.vue';
// import InputCheckbox from './InputCheckbox.vue';

/**
 * @description Название события отправки документа.
 * @type {string}
 */
const NAME_SUBMIT_DOCUMENT_EVENT = 'submit-document';

/**
 * @description Название события отправки договора.
 * @type {string}
 */
const NAME_SUBMIT_CONTRACT_EVENT = 'submit-contract';

/**
 * @description Название события загрузки файла.
 * @type {string}
 */
const NAME_DOWNLOAD_FILE_EVENT = 'download-file';

/**
 * @description Название события проверки договора.
 * @type {string}
 */
const NAME_PREVIEW_CONTRACT_EVENT = 'preview-contract';

/**
 * @description Название события проверки документа.
 * @type {string}
 */
const NAME_PREVIEW_DOCUMENT_EVENT = 'preview-document';

/**
 * @description Название события "Скачать переменные".
 * @type {string}
 */
const NAME_DOWNLOAD_VARIABLES_EVENT = 'download-variables';

const acceptList = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.docx',
  'application/vnd.oasis.opendocument.text',
  '.odt',
].join(', ');

const MAX_SIZE_FILE = 1024 * 1024 * 100;

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

export default {
  /**
    * @description Компонент формы создания документа.
    */
  name: 'FormCreateClinicDocumentTemplate',
  components: {
    // InputCheckbox,
    AutocompleteInput,
    TemplateVariablesInfo,
    InputSelect,
    InputText,
    InputFile,
  },
  acceptList,
  computed: {
    disableUpload: ({ file, isAwaitRequest }) => file === null || isAwaitRequest,
    fileInputErrors: resolveInputErrors('file'),
    nameInputErrors: resolveInputErrors('name'),
    categoryInputErrors: resolveInputErrors('category-id'),
  },
  methods: {
    /**
     * @description Обработать событие нажатия на кнопку "Скачать переменные".
     * @returns {void}
     */
    onClickDownloadVariables() {
      this.$emit(NAME_DOWNLOAD_VARIABLES_EVENT);
    },
    /**
     * @description Проверить файл.
     * @returns {boolean}
     */
    validateFile() {
      if (this.file.size > MAX_SIZE_FILE) {
        this.notifyFail('Файл сильно большой.');
        return false;
      }
      return true;
    },
    /**
     * @description Обработать событие просмотра файла.
     * @returns {void}
     */
    onViewFile() {
      if (!this.validateFile()) {
        return;
      }
      if (this.type === DOCUMENT_TYPE) {
        this.$emit(
          NAME_PREVIEW_DOCUMENT_EVENT,
          new ClinicTemplateRequestPreview('', this.file),
        );
      }
      if (this.type === CONTRACT_TYPE) {
        this.$emit(
          NAME_PREVIEW_CONTRACT_EVENT,
          new ClinicTemplateRequestPreview('', this.file),
        );
      }
    },
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      if (!this.validateFile()) {
        return;
      }
      if (this.type === DOCUMENT_TYPE) {
        this.$emit(
          NAME_SUBMIT_DOCUMENT_EVENT,
          new ClinicTemplateRequestCreate(
            this.name,
            this.file,
            this.categoryId,
            this.isEsiaConfirmationRequired,
          ),
        );
      }
      if (this.type === CONTRACT_TYPE) {
        this.$emit(
          NAME_SUBMIT_CONTRACT_EVENT,
          new ClinicTemplateRequestCreate(
            this.name,
            this.file,
            this.categoryId,
            this.isEsiaConfirmationRequired,
          ),
        );
      }
    },
    /**
     * @description Обработать событие загрузки файла.
     * @returns {void}
     */
    onDownloadFile() {
      this.$emit(NAME_DOWNLOAD_FILE_EVENT, this.file);
    },
  },
  data() {
    return {
      /**
       * @description Название.
       * @type {String}
       */
      name: '',
      /**
       * @description Id категории.
       * @type {string}
       */
      categoryId: '',
      /**
       * @description Файл.
       * @type {File|null}
       */
      file: null,
      /**
       *
       * @description Флаг. Необходимо ли подтверждение в ЕСИА
       * @type {boolean}
       */
      isEsiaConfirmationRequired: false,
    };
  },
  props: {
    /**
     * @description Категории.
     * @type Array
     */
    categories: { type: Array, required: true },
    /**
     * @description Нарушения ограничений.
     * @type {any}
     */
    inputErrors: { type: Object, required: true },
  },
  mixins: [
    clinicTemplateMixin,
    notifierMixin,
  ],
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";
.form-create-clinic-document-template {
  display: grid;
  gap: 10px;
  padding-block: 20px;
  &__addiction-info {
    color: gray;
    font-size: 12px;
  }
  &__buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px;
    max-width: 438px;
    width: 100%;
    margin: 30px 0 0 auto;
    &-submit {
      @include buttons.btn-blue(100%, 3rem, 1rem);
      &:disabled {
        background-color: var(--unfocus-color);
      }
    }
    &-cancel {
      @include buttons.btn-transparent(100%, 3rem, 1rem);
    }
  }
}
</style>
